<template>
    <div
        class="map-container w-screen h-screen rounded-md overflow-hidden"
        style="transform: translate3d(0, 0, 150px)"
    >
        <slot name="actionbar" v-if="$slots.actionbar">

            <NavFilterControls
                :mapType="mapType"
                @handleSelect="handleSelect"
            />
        </slot>
        <button
            type="button"
            class="bg-white rounded border border-slate-200 text-slate-800 text-sm font-medium shadow-sm px-3 py-1.5 flex items-center gap-2 absolute top-4 md:top-6 right-4 md:right-6 z-1050 hover:border-slate-300 hover:bg-slate-200 active:border-slate-300 active:bg-slate-200 focus:border-slate-200 focus:outline-slate-200"
            tabindex="0"
            @click="$emit('closeMap')"
            style="transform: translate3d(0, 0, 170px)"
        >
            <span>Close map</span>
            <Icon icon="close" :width="14" :height="14" />
        </button>

        <ZoomControls
            @zoomIn="zoomIn"
            @zoomOut="zoomOut"
            @reCenter="reCenter"
        />
        <div
            id="map-results"
            class="w-[100vw] h-[100vh]"

        ></div>
        <transition name="fade">
            <slot
                name="popup"
                :infoWindowContent="infoWindowContent"
                :show="showInfoWindow"
                :closeHandler="closeInfoWindow"
            >
                <InfoWindow
                    @closeInfoWindow="closeInfoWindow"
                    v-if="showInfoWindow"
                    :infoWindowContent="infoWindowContent"
                    :mapType="mapType"
                />
            </slot>
        </transition>
        <transition name="fade">
            <div
                class="absolute inset-0 bg-slate-900 flex items-center justify-center bg-opacity-40 backdrop-blur-sm z-50"
                style="transform: translate3d(0, 0, 150px)"
                v-if="loading"
            >
                <LoadingState :loading="loading" />
            </div>
        </transition>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch, computed } from 'vue';
import { useMapsStore } from '@/store/maps';
import InfoWindow from '@/components/hotels/search/map-results/info-window.vue';
import NavFilterControls from '@/components/hotels/search/map-results/nav-filter-controls.vue';
import ZoomControls from '@/components/hotels/search/map-results/zoom-controls.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    loading: {
        type: Boolean,
        default: false,
    },
    mapCenter: {
        type: Object,
        required: false,
    },
    mapMarkers: {
        type: Array,
        required: true,
    },
    mapType: {
        type: String,
        required: true,
    },
    showMap: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['closeMap']);
const mapsStore = useMapsStore();
const { generateMapMarkerHtml } = mapsStore;
const { Map } = await google.maps.importLibrary('maps');
const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
const infoWindowContent = ref({});
const showInfoWindow = ref(false);
let map, bounds, currentMapCenter;
let pins = [];
let viewportWidth = window.innerWidth;
const mobileWidth = 768;
let mapCenterOffsetX = 0;
let mapCenterOffsetY = 0;

if (viewportWidth < mobileWidth) {
    mapCenterOffsetX = 0;
    mapCenterOffsetY = 180;
} else {
    mapCenterOffsetX = 100;
    mapCenterOffsetY = 100;
}

// const showSearchResultsOnMap = () => {
//     let val = !showOnMap;
//     if (!val) {
//         setMapType('search');
//     }
//     setShowOnMap(val);
//     goBack();
// };

// const pinIcon = computed(() => {
//     if (props.mapType == 'searchResults' || props.mapType == 'wizardSearch') {
//         return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
//             <path d="M10.648 2.79547L11.7799 4.09818C11.9742 4.32178 12.27 4.42945 12.5625 4.38304L14.267 4.11266C14.7824 4.0309 15.251 4.42408 15.26 4.9459L15.2897 6.67138C15.2948 6.96756 15.4522 7.24018 15.7061 7.39269L17.1856 8.28115C17.633 8.54984 17.7392 9.15223 17.4107 9.55774L16.3243 10.8986C16.1378 11.1288 16.0832 11.4388 16.1797 11.7189L16.7419 13.3505C16.912 13.8439 16.6061 14.3736 16.0938 14.4731L14.3997 14.8019C14.1089 14.8584 13.8677 15.0607 13.7616 15.3373L13.1436 16.9486C12.9567 17.4359 12.3819 17.6451 11.9255 17.3919L10.4163 16.5549C10.1573 16.4112 9.84246 16.4112 9.58342 16.5549L8.07425 17.3919C7.61786 17.6451 7.04306 17.4359 6.85615 16.9486L6.23809 15.3373C6.132 15.0607 5.89084 14.8584 5.60005 14.8019L3.90594 14.4731C3.39362 14.3736 3.08777 13.8439 3.2578 13.3505L3.82004 11.7189C3.91655 11.4388 3.86188 11.1288 3.67541 10.8986L2.58904 9.55774C2.26051 9.15223 2.36673 8.54984 2.81414 8.28115L4.2936 7.39269C4.54755 7.24018 4.70495 6.96756 4.71005 6.67138L4.73975 4.9459C4.74873 4.42408 5.21731 4.0309 5.73276 4.11266L7.43719 4.38304C7.72975 4.42945 8.02557 4.32178 8.21985 4.09818L9.35172 2.79547C9.69402 2.40151 10.3057 2.40151 10.648 2.79547Z" fill="#FEF0E7"/>
//             <path d="M7.91653 10.4169L8.87191 11.3722C9.03463 11.535 9.29844 11.535 9.46116 11.3722L12.0832 8.75019M7.43719 4.38304L5.73276 4.11266C5.21731 4.0309 4.74873 4.42408 4.73975 4.9459L4.71005 6.67138C4.70495 6.96756 4.54755 7.24018 4.2936 7.39269L2.81414 8.28115C2.36673 8.54984 2.26051 9.15223 2.58904 9.55774L3.67541 10.8986C3.86188 11.1288 3.91655 11.4388 3.82004 11.7189L3.2578 13.3505C3.08777 13.8439 3.39362 14.3736 3.90594 14.4731L5.60005 14.8019C5.89084 14.8584 6.132 15.0607 6.23809 15.3373L6.85615 16.9486C7.04306 17.4359 7.61786 17.6451 8.07425 17.3919L9.58342 16.5549C9.84246 16.4112 10.1573 16.4112 10.4163 16.5549L11.9255 17.3919C12.3819 17.6451 12.9567 17.4359 13.1436 16.9486L13.7616 15.3373C13.8677 15.0607 14.1089 14.8584 14.3997 14.8019L16.0938 14.4731C16.6061 14.3736 16.912 13.8439 16.7419 13.3505L16.1797 11.7189C16.0832 11.4388 16.1378 11.1288 16.3243 10.8986L17.4107 9.55774C17.7392 9.15223 17.633 8.54984 17.1856 8.28115L15.7061 7.39269C15.4522 7.24018 15.2948 6.96756 15.2897 6.67138L15.26 4.9459C15.251 4.42408 14.7824 4.0309 14.267 4.11266L12.5625 4.38304C12.27 4.42945 11.9742 4.32178 11.7799 4.09818L10.648 2.79547C10.3057 2.40151 9.69402 2.40151 9.35172 2.79547L8.21985 4.09818C8.02557 4.32178 7.72975 4.42945 7.43719 4.38304Z" stroke="#FD7615" stroke-width="1.5" stroke-linecap="round"/>
//             </svg>`;
//     } else {
//         return '';
//     }
// });

const mapOptions = {
    mapId: '4504f8b373AF365c3d0',
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoom: 14,
};

watch(
    () => props.mapMarkers,
    () => {
        showInfoWindow.value = false;
        clearMarkers();

        if (props.mapMarkers.length > 0) {
            loadMarkers();
        }
    }
);

const clearMarkers = () => {
    pins.forEach((pin) => {
        pin.setMap(null);
    });
    pins = [];
};

google.maps.Map.prototype.setCenterWithOffset = function (
    latlng,
    offsetX,
    offsetY
) {
    var map = this;
    var ov = new google.maps.OverlayView();
    ov.onAdd = function () {
        var proj = this.getProjection();
        var aPoint = proj.fromLatLngToContainerPixel(latlng);
        aPoint.x = aPoint.x + offsetX;
        aPoint.y = aPoint.y + offsetY;
        map.panTo(proj.fromContainerPixelToLatLng(aPoint));
    };
    ov.draw = function () {};
    ov.setMap(this);
};

const resetActiveMarkerTags = () => {
    let $markerTags = document.querySelectorAll('.marker-tag');
    $markerTags.forEach((tag) => {
        tag.classList.remove('active');
    });
};

const loadMarkers = () => {
    bounds = new google.maps.LatLngBounds();

    props.mapMarkers.forEach((marker) => {
        const position = new google.maps.LatLng(marker[1], marker[2]);
        bounds.extend(position);

        const markerTag = document.createElement('div');
        markerTag.className = 'marker-tag';
        markerTag.innerHTML = generateMapMarkerHtml(
            marker,
            props.pinIcon ? props.pinIcon : ''
        );

        const pin = new AdvancedMarkerElement({
            position: position,
            map: map,
            title: marker[0],
            content: markerTag,
        });

        pin.addListener('click', () => {
            //map.panTo(position);
            map.setCenterWithOffset(
                position,
                mapCenterOffsetX,
                mapCenterOffsetY
            );
            infoWindowContent.value = marker[3];
            showInfoWindow.value = true;
            resetActiveMarkerTags();
            markerTag.classList.add('active');
        });
        pins.push(pin);
    });

    map.fitBounds(bounds);

    google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
        if (props.mapMarkers.length === 1) {
            map.setZoom(16);
        }

        if (props.mapType === 'property' && props.mapCenter) {
            currentMapCenter = props.mapCenter;
            map.setCenter(currentMapCenter);
        }
        currentMapCenter = null;
    });

    checkForOnePin();
};

const initMap = () => {
    map = new Map(document.getElementById('map-results'), mapOptions);

    map.setCenter(new google.maps.LatLng(37.0902, 95.7129));

    loadMarkers();

    google.maps.event.addListener(map, 'resize', () => {
        currentMapCenter = map.getCenter();
        map.setCenter(currentMapCenter);
    });

    window.addEventListener(
        'resize',
        () => {
            google.maps.event.trigger(map, 'resize');
        },
        true
    );
    map.fitBounds(bounds);
};

const zoomIn = () => {
    map.setZoom(map.getZoom() + 1);
};

const zoomOut = () => {
    map.setZoom(map.getZoom() - 1);
};

const closeInfoWindow = () => {
    showInfoWindow.value = false;
    let $markerTags = document.querySelectorAll('.marker-tag');
    $markerTags.forEach((tag) => {
        tag.classList.remove('active');
    });
};

const reCenter = () => {
    closeInfoWindow();
    map.fitBounds(bounds);
};

const goBack = () => {
    if (mapType.value === 'favorites') {
        clearSelectedGroups();
    }
};

const checkForOnePin = () => {
    setTimeout(() => {
        if (pins.length === 1 && viewportWidth > mobileWidth) {
            new google.maps.event.trigger(pins[0], 'click');
        }
        if (pins.length !== 1) {
            showInfoWindow.value = false;
        }
    }, 200);
};

const handleSelect = (data) => {
    resetActiveMarkerTags();
    checkForOnePin();
};

window.addEventListener('resize', () => {
    viewportWidth = window.innerWidth;

    if (viewportWidth < mobileWidth) {
        mapCenterOffsetX = 0;
        mapCenterOffsetY = 180;
    } else {
        mapCenterOffsetX = 100;
        mapCenterOffsetY = 0;
    }
});

onMounted(() => {
    initMap();
});
</script>

<style scoped>
.map-container {
    position: fixed;
    inset: 0;
    z-index: 1000; /* High enough to stay above the map */
    isolation: isolate; /* Ensures a clean stacking context */
}

#map-results {
    position: relative;
    z-index: 900; /* Lower than buttons and slot content */
}

button {
    position: absolute;
    z-index: 1050; /* Higher than map and any backdrop */
}

.slot-container {
    position: absolute;
    inset: 0;
    z-index: 1050; /* Higher than map results */
    pointer-events: auto; /* Ensures interactivity */
    transform: translateZ(0); /* Forces hardware rendering for Safari */
}

</style>
